import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shirt, ShoppingBag, Sparkles, UtensilsCrossed, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { REQUEST_TYPE, type RequestType } from '@hotel-voice/shared';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, ListSkeleton } from '@/components/common/StateView';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useReadyGuest } from '@/contexts/GuestSessionContext';
import { useMyRequests } from '@/contexts/RequestsContext';
import { formatCurrency } from '@/lib/money';
import { cancelServiceRequest } from '@/services/firebase/callables';
import { toUserMessage } from '@/utils/errors';
import type { GuestRequest } from '@/types';
import { STATUS_PRESENTATION, TYPE_LABEL, canGuestCancel, summarizeItems } from './presentation';

const TYPE_ICON: Record<RequestType, LucideIcon> = {
  [REQUEST_TYPE.HOUSEKEEPING]: Sparkles,
  [REQUEST_TYPE.LAUNDRY]: Shirt,
  [REQUEST_TYPE.RESTAURANT]: UtensilsCrossed,
  [REQUEST_TYPE.STORE]: ShoppingBag,
};

function formatWhen(request: GuestRequest): string {
  const date = request.createdAt?.toDate();
  if (!date) return 'Just now';
  return date.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit', day: 'numeric', month: 'short' });
}

/**
 * Everything this guest has asked for, newest first, live. A request still at
 * REQUESTED can be withdrawn; after staff accept it the guest talks to the
 * desk instead.
 */
export function MyRequestsPage() {
  const { location } = useReadyGuest();
  const { requests, error, retry } = useMyRequests();
  const [cancelling, setCancelling] = useState<GuestRequest | null>(null);
  const [busy, setBusy] = useState(false);

  const confirmCancel = async () => {
    if (!cancelling || busy) return;
    setBusy(true);
    try {
      await cancelServiceRequest({
        hotelId: location.hotelId,
        roomId: location.roomId,
        requestId: cancelling.id,
      });
      toast.success('Request cancelled');
      setCancelling(null);
    } catch (err) {
      toast.error(toUserMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const body = () => {
    if (error) return <ErrorState message={error} onRetry={retry} />;
    if (requests === null) return <ListSkeleton rows={4} />;
    if (requests.length === 0) {
      return (
        <EmptyState title="No requests yet" message="Anything you order or ask for will show up here." />
      );
    }

    return (
      <ul className="flex flex-col gap-3 p-4">
        {requests.map((request) => {
          const Icon = TYPE_ICON[request.type];
          const status = STATUS_PRESENTATION[request.status];
          return (
            <li key={request.id}>
              <Card className="gap-2 py-3">
                <CardContent className="flex items-start gap-3">
                  <span className="bg-muted text-primary flex size-10 shrink-0 items-center justify-center rounded-xl">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold">{TYPE_LABEL[request.type]}</p>
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </div>
                    <p className="text-muted-foreground mt-0.5 line-clamp-2 text-sm">
                      {summarizeItems(request.items, request.note)}
                    </p>
                    {request.additionalCharges?.length ? (
                      <p className="text-muted-foreground mt-0.5 text-xs">
                        + {request.additionalCharges.map((c) => `${c.name} ${formatCurrency(c.amount)}`).join(', ')}
                      </p>
                    ) : null}
                    {request.guestNotification?.body && (
                      <p className="bg-muted text-foreground mt-2 rounded-lg px-2 py-1.5 text-xs">
                        {request.guestNotification.body}
                      </p>
                    )}
                    <div className="text-muted-foreground mt-1 flex items-center justify-between text-xs">
                      <span>{formatWhen(request)}</span>
                      {request.totalCharge > 0 && (
                        <span className="text-foreground font-medium tabular-nums">
                          {formatCurrency(request.totalCharge)}
                          {request.chargesConfirmedAt ? ' · confirmed' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
                {canGuestCancel(request.status) && (
                  <CardContent className="flex justify-end">
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setCancelling(request)}>
                      Cancel request
                    </Button>
                  </CardContent>
                )}
              </Card>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <AppShell>
      <PageHeader
        title="My Requests"
        right={
          <Button asChild size="sm" variant="outline">
            <Link to="/">New request</Link>
          </Button>
        }
      />
      {body()}

      <Dialog open={cancelling !== null} onOpenChange={(open) => !open && !busy && setCancelling(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel this request?</DialogTitle>
            <DialogDescription>
              {cancelling ? `${TYPE_LABEL[cancelling.type]} — ${summarizeItems(cancelling.items, cancelling.note)}` : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelling(null)} disabled={busy}>
              Keep it
            </Button>
            <Button variant="destructive" onClick={confirmCancel} disabled={busy} aria-busy={busy}>
              {busy ? 'Cancelling…' : 'Cancel request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
